/**
 * The academy's classes, as seeded on first run.
 *
 * Prices are editable in Settings afterwards — the seed only fills an empty
 * database, so adding a class in a future release seeds just that one without
 * resetting prices the owner has already changed.
 *
 * The academy runs a single location, so there is no branch concept anywhere
 * in the system.
 */
import type { ClassDef } from './types';

export const CLASSES: readonly ClassDef[] = [
  { key: 'kickboxing_adults', display: 'Kickboxing', ageGroup: 'Adults' },
  { key: 'kickboxing_kids', display: 'Kickboxing', ageGroup: 'Kids' },
  { key: 'boxing_adults', display: 'Boxing', ageGroup: 'Adults' },
  { key: 'boxing_kids', display: 'Boxing', ageGroup: 'Kids' },
  { key: 'judo_adults', display: 'Judo', ageGroup: 'Adults' },
  { key: 'judo_kids', display: 'Judo', ageGroup: 'Kids' },
  { key: 'jiujitsu_adults', display: 'Jiu-Jitsu', ageGroup: 'Adults' },
  { key: 'jiujitsu_kids', display: 'Jiu-Jitsu', ageGroup: 'Kids' },
  { key: 'mma_adults', display: 'MMA', ageGroup: 'Adults' },
  { key: 'strength_cond_adults', display: 'Strength & Conditioning', ageGroup: 'Adults' },
  { key: 'fitness_kids', display: 'Fitness', ageGroup: 'Kids' },
  { key: 'muaythai_adults', display: 'Muay Thai', ageGroup: 'Adults' },
  { key: 'wrestling_adults', display: 'Wrestling', ageGroup: 'Adults' },
  { key: 'womens_kickboxing', display: "Women's Kickboxing", ageGroup: 'Adults' },
] as const;

const CLASS_BY_KEY = new Map(CLASSES.map((c) => [c.key, c]));

export function findClass(key: string): ClassDef | undefined {
  return CLASS_BY_KEY.get(key);
}

/**
 * Display name for a class key, e.g. `kickboxing_kids` -> "Kickboxing (Kids)".
 * Falls back to the raw key so an unknown class from a newer device still
 * renders something meaningful instead of blank.
 */
export function classLabel(key: string): string {
  const def = CLASS_BY_KEY.get(key);
  return def ? `${def.display} (${def.ageGroup})` : key;
}

/** Classes grouped for the picker: Adults first, then Kids. */
export function classesByAgeGroup(): { ageGroup: string; classes: ClassDef[] }[] {
  return [
    { ageGroup: 'Adults', classes: CLASSES.filter((c) => c.ageGroup === 'Adults') },
    { ageGroup: 'Kids', classes: CLASSES.filter((c) => c.ageGroup === 'Kids') },
  ];
}
